import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/ui/password-input';
import { AuthLayoutWrapper } from '@/components/auth/AuthLayoutWrapper';
import logoImage from '@/assets/prsnl-logo.svg';
import authIllustration from '@/assets/auth/auth-illustration.jpg';
import { signInWithPassword, signInWithOtp, signOut, getUser } from '@/services/data/auth';
import { getUserById } from '@/services/data/users';

interface LoginScreenProps {
  onLogin: () => void;
  onNavigateToSignup: () => void;
  onNavigateToForgotPassword?: () => void;
  initialSuccessMessage?: string;
}

export function LoginScreen({
  onLogin,
  onNavigateToSignup,
  onNavigateToForgotPassword,
  initialSuccessMessage = ''
}: LoginScreenProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [useMagicLink, setUseMagicLink] = useState(false);
  const [successMessage, setSuccessMessage] = useState(initialSuccessMessage);

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email) || email.length > 254) {
      setError('Veuillez entrer une adresse email valide');
      return;
    }
    if (!password) {
      setError('Veuillez entrer votre mot de passe');
      return;
    }
    setIsLoading(true);
    try {
      const result = await signInWithPassword(email, password);
      if (!result.success) {
        setError('Email ou mot de passe incorrect');
        setIsLoading(false);
        return;
      }
      
      // Get the authenticated user to check is_active
      const authUser = await getUser();
      if (authUser.success && authUser.data?.id) {
        const userResult = await getUserById(authUser.data.id);
        if (userResult.success && userResult.data && userResult.data.is_active === false) {
          // User is deactivated - sign them out and show error
          await signOut();
          setError('Ce compte a été désactivé. Veuillez créer un nouveau compte.');
          setIsLoading(false);
          return;
        }
      }
      
      onLogin();
    } catch {
      setError('Une erreur inattendue est survenue');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMagicLinkLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email) || email.length > 254) {
      setError('Veuillez entrer une adresse email valide');
      return;
    }
    setIsLoading(true);
    try {
      const result = await signInWithOtp(email, window.location.origin);
      if (!result.success) {
        setError(result.error?.message || 'Une erreur est survenue lors de l\'envoi du lien');
        setIsLoading(false);
        return;
      }
      setSuccessMessage('Un lien de connexion a été envoyé à votre adresse email. Vérifiez votre boîte de réception.');
      setEmail('');
      setPassword('');
    } catch {
      setError('Une erreur inattendue est survenue');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayoutWrapper illustrationSrc={authIllustration}>
      <div className="text-center">
        <img src={logoImage} alt="PRSNL" className="w-40 h-auto mx-auto mb-12 brightness-0 invert" />
        
        <form onSubmit={useMagicLink ? handleMagicLinkLogin : handlePasswordLogin} className="space-y-6">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-4">
              <p className="text-sm text-red-300">{error}</p>
            </div>
          )}
          
          {successMessage && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-2xl p-4">
              <p className="text-sm text-green-300">{successMessage}</p>
            </div>
          )}
          
          <div className="space-y-3 text-left">
            <label htmlFor="email" className="text-white text-sm block mb-2">Email</label>
            <Input 
              id="email" 
              type="email" 
              placeholder="votre@email.com" 
              value={email} 
              onChange={e => setEmail(e.target.value)} 
              className="bg-white/5 backdrop-blur-sm border-white/10 text-white rounded-xl h-12 px-5 text-base placeholder:text-white/30" 
              disabled={isLoading} 
            />
          </div>

          {!useMagicLink && (
            <div className="space-y-3 text-left">
              <label htmlFor="password" className="text-white text-sm block mb-2">Mot de passe</label>
              <PasswordInput 
                id="password" 
                placeholder="••••••••" 
                value={password} 
                onChange={e => setPassword(e.target.value)} 
                className="bg-white/5 backdrop-blur-sm border-white/10 text-white rounded-xl h-12 px-5 text-base placeholder:text-white/30" 
                disabled={isLoading} 
              />
            </div>
          )}

          <Button 
            type="submit" 
            disabled={isLoading || successMessage !== ''} 
            className="w-full h-12 rounded-xl text-base font-medium bg-neutral-700 text-white hover:bg-neutral-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Connexion...' : useMagicLink ? 'Envoyer le lien' : 'Se connecter'}
          </Button>

          {!useMagicLink && onNavigateToForgotPassword && (
            <button 
              type="button"
              onClick={onNavigateToForgotPassword}
              className="text-sm text-white/60 hover:text-white transition-colors"
            >
              Mot de passe oublié ?
            </button>
          )}
        </form>

        <div className="mt-6">
          <button onClick={onNavigateToSignup} className="text-sm text-white/60 hover:text-white transition-colors">
            Pas encore de compte ? <span className="underline">S'inscrire</span>
          </button>
        </div>
      </div>
    </AuthLayoutWrapper>
  );
}